<template>
  <div class="project-scope-display">
    <p v-if="syncedAt" class="project-scope-display__synced">
      Gesynchroniseerd op {{ formatDate(syncedAt) }}
    </p>

    <ProductSpecsAccordion
      :items="normalizedItems"
      empty-text="Geen specificaties beschikbaar."
    />

    <!-- Situatiefoto's from scope (per product) -->
    <div v-if="scopeSituationPhotos.length" class="project-scope-display__photos">
      <SituationPhotos
        :photos="scopeSituationPhotos"
        :editable="false"
      />
    </div>
  </div>
</template>

<script>
  import { defineComponent, computed } from 'vue';
  import ProductSpecsAccordion from '@/components/partials/ProductSpecsAccordion.vue';
  import SituationPhotos from '@/components/partials/SituationPhotos.vue';

  export default defineComponent({
    name: 'ProjectScopeDisplay',

    props: {
      scope: {
        type: Object,
        default: null,
      },
      scopeSyncedAt: {
        type: String,
        default: null,
      },
      formatDateFn: {
        type: Function,
        default: (d) => (d ? new Date(d).toLocaleDateString('nl-NL') : ''),
      },
    },

    components: { ProductSpecsAccordion, SituationPhotos },

    setup(props) {
      const formatDate = (d) => props.formatDateFn(d);

      const scopeItems = computed(() => {
        const enriched = props.scope?._enriched || props.scope;
        if (!enriched) return [];

        const items = [];

        if (enriched.selectedProduct) {
          const product = enriched.selectedProduct;
          const options = (enriched.productOptions || [])
            .filter((o) => o.displayValue != null && o.displayValue !== '')
            .map((o) => ({ label: o.optionLabel, value: o.displayValue }));

          items.push({
            productName: product.name || 'Product',
            options,
            situationPhotos: enriched.situation_photos || [],
            totalPrice:
              enriched.pricingSnapshot?.totalPrice ??
              enriched.pricingSnapshot?.finalPrice ??
              enriched.pricingSnapshot?.total,
          });
        }

        if (
          !enriched.selectedProduct &&
          enriched.products &&
          Array.isArray(enriched.products)
        ) {
          enriched.products.forEach((p) => {
            const options = (p.options || [])
              .filter((o) => o.displayValue != null && o.displayValue !== '')
              .map((o) => ({ label: o.optionLabel, value: o.displayValue }));
            items.push({
              productName: p.productName || p.name || 'Product',
              options,
              situationPhotos: p.situation_photos || [],
              totalPrice: p.totalPrice,
              source: p.source || p.metadata?.source || null,
            });
          });
        }

        return items;
      });

      const normalizedItems = computed(() => {
        return scopeItems.value.map((item) => {
          // Catalog products were added straight from the catalog without a
          // configurator run — they have no options/pricing breakdown.
          const isCatalog = item.source === 'catalog';
          return {
            name: item.productName,
            price: item.totalPrice,
            specs: item.options,
            badge: isCatalog ? 'Catalogus' : null,
            badgeVariant: isCatalog ? 'catalog' : null,
            emptyNote: isCatalog
              ? 'Dit product komt rechtstreeks uit de catalogus en is niet geconfigureerd — er zijn geen specificaties.'
              : null,
          };
        });
      });

      const scopeSituationPhotos = computed(() => {
        const photos = [];
        scopeItems.value.forEach((item) => {
          (item.situationPhotos || []).forEach((photo) => {
            photos.push({
              ...photo,
              type: 'product',
              product_name: item.productName,
            });
          });
        });
        return photos;
      });

      return {
        formatDate,
        syncedAt: computed(() => props.scopeSyncedAt),
        normalizedItems,
        scopeSituationPhotos,
      };
    },
  });
</script>

<style lang="scss" scoped>
  .project-scope-display {
    font-size: 0.875rem;

    &__synced {
      margin: 0 0 0.75rem;
      color: var(--slate-500);
      font-size: 0.8125rem;
    }

    &__photos {
      margin-top: 1rem;
      padding-top: 0.75rem;
      border-top: 1px solid var(--slate-100);
    }
  }
</style>
